import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from './common/Button';
import { LoadingSpinner } from './common/LoadingSpinner';
import { theme } from '../styles/theme';
import { apiEndpoint } from '../config';
import { Link } from 'react-router-dom';

// Reuse styled components from SimpleDemoComparison where possible
const DemoContainer = styled.div`
  padding: ${theme.spacing['2xl']};
  max-width: 1400px;
  margin: 0 auto;
`;

const Title = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  color: ${theme.colors.text.primary};
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`;

const ProviderSelector = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const ProviderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
`;

const ProviderCard = styled.div<{ $selected: boolean }>`
  background: ${props => props.$selected ? theme.colors.primary : 'white'};
  color: ${props => props.$selected ? 'white' : theme.colors.text.primary};
  border: 2px solid ${props => props.$selected ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.md};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ProviderTitle = styled.div`
  font-weight: 600;
  margin-bottom: ${theme.spacing.xs};
`;

const ProviderDescription = styled.div`
  font-size: ${theme.fontSizes.sm};
  opacity: 0.9;
`;

const ApiKeyInputs = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
`;

const ApiKeyField = styled.div``;

const ApiKeyLabel = styled.label`
  display: block;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.xs};
`;

const ApiKeyInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-family: monospace;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const PromptInput = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  font-family: ${theme.fonts.body};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: center;
  margin-bottom: ${theme.spacing.xl};
`;

const ViewButton = styled.button<{ $active: boolean }>`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  background: ${props => props.$active ? theme.colors.primary : 'white'};
  color: ${props => props.$active ? 'white' : theme.colors.text.primary};
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.radii.md};
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  
  &:hover {
    background: ${props => props.$active ? theme.colors.primaryDark : theme.colors.primaryLight};
  }
`;

const ComparisonContainer = styled.div``;

const LLMComparisonGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.xl};
`;

const LLMSection = styled.div`
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing.xl};
  background: ${theme.colors.backgroundSecondary};
`;

const LLMHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const PromptVersionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${theme.spacing.lg};
`;

const VersionCard = styled.div<{ $isWinner?: boolean }>`
  background: ${theme.colors.background};
  border: 2px solid ${props => props.$isWinner ? '#10b981' : theme.colors.border};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.lg};
`;

const CardTitle = styled.h4`
  font-size: ${theme.fontSizes.base};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const PromptText = styled.div`
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.radii.sm};
  font-family: 'Monaco', monospace;
  font-size: ${theme.fontSizes.xs};
  line-height: 1.5;
  max-height: 150px;
  overflow-y: auto;
  white-space: pre-wrap;
  margin-bottom: ${theme.spacing.md};
`;

const ResponseText = styled.div`
  background: white;
  border: 1px solid ${theme.colors.borderLight};
  padding: ${theme.spacing.md};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.sm};
  line-height: 1.6;
  max-height: 250px;
  overflow-y: auto;
  white-space: pre-wrap;
`;

const SectionLabel = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.muted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${theme.spacing.xs};
`;

const Score = styled.div`
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ScoreValue = styled.span`
  font-size: ${theme.fontSizes.lg};
  font-weight: bold;
  color: ${theme.colors.primary};
`;

const ComparisonAnalysis = styled.div`
  margin-top: ${theme.spacing['2xl']};
  padding: ${theme.spacing.xl};
  background: linear-gradient(135deg, ${theme.colors.primaryLight} 0%, ${theme.colors.backgroundSecondary} 100%);
  border-radius: ${theme.radii.lg};
  border: 1px solid ${theme.colors.border};
`;

const AnalysisTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const AnalysisContent = styled.div`
  background: white;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.radii.md};
  line-height: 1.8;
  white-space: pre-wrap;
`;

const ErrorMessage = styled.div`
  background: #fee;
  border: 1px solid #f44;
  color: #c00;
  padding: ${theme.spacing.md};
  border-radius: ${theme.radii.md};
  margin-top: ${theme.spacing.md};
`;

const UpgradeBanner = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primaryLight} 0%, ${theme.colors.primary} 100%);
  color: white;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.radii.md};
  margin: ${theme.spacing.xl} 0;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const UpgradeTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  margin-bottom: ${theme.spacing.sm};
`;

const UpgradeDescription = styled.p`
  font-size: ${theme.fontSizes.base};
  margin-bottom: ${theme.spacing.md};
  opacity: 0.95;
`;

interface Props {
  userTier?: 'free' | 'raptor' | 'rex';
  isAuthenticated?: boolean;
}

export const AdvancedDemoComparison: React.FC<Props> = ({ 
  userTier = 'free', 
  isAuthenticated = false 
}) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');
  const [selectedProviders, setSelectedProviders] = useState<string[]>(['openai']);
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [viewMode, setViewMode] = useState<'by-prompt' | 'by-llm'>('by-prompt');
  const [useOwnKeys, setUseOwnKeys] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  const canCompareLLMs = userTier === 'rex' || (userTier === 'raptor' && isAuthenticated);

  const toggleProvider = (provider: string) => {
    // Show upgrade prompt for free users trying to select multiple providers
    if (!canCompareLLMs && (provider === 'both' || (selectedProviders.length >= 1 && !selectedProviders.includes(provider)))) {
      setShowUpgradePrompt(true);
      return;
    }
    
    if (provider === 'both' && canCompareLLMs) {
      setSelectedProviders(['openai', 'anthropic']);
    } else if (selectedProviders.includes(provider)) {
      setSelectedProviders(selectedProviders.filter(p => p !== provider));
    } else {
      if (canCompareLLMs) {
        setSelectedProviders([...selectedProviders, provider]);
      } else {
        setSelectedProviders([provider]);
      }
    }
  };

  const runComparison = async () => {
    if (!prompt.trim()) return;
    
    setLoading(true);
    setError('');
    setResults(null);
    
    try {
      const llmProvider = canCompareLLMs && selectedProviders.length > 1 
        ? 'both' 
        : selectedProviders[0] || 'openai';
      
      const response = await fetch(apiEndpoint('/api/demo/compare'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          improvement_type: 'both',
          llm_provider: llmProvider,
          api_key: useOwnKeys ? openaiKey : undefined,
          anthropic_api_key: useOwnKeys ? anthropicKey : undefined,
          user_tier: userTier,
          use_demo_key: !useOwnKeys,
          compare_llms: canCompareLLMs && selectedProviders.length > 1
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed: ${response.statusText}`);
      }
      
      const data = await response.json();
      setResults(data);
      
      // If we have both LLMs, generate comparative analysis
      if (data.llm_responses && data.llm_responses.length > 3) {
        await generateComparativeAnalysis(data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to run comparison');
    } finally {
      setLoading(false);
    }
  };

  const generateComparativeAnalysis = async (data: any) => {
    try {
      // Call a new endpoint to generate comparative analysis
      const response = await fetch(apiEndpoint('/api/demo/analyze'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          demo_id: data.demo_id,
          responses: data.llm_responses,
          prompts: [data.original_prompt, ...data.improved_prompts]
        })
      });
      
      if (response.ok) {
        const analysis = await response.json();
        setResults((prev: any) => ({ ...prev, comparative_analysis: analysis.analysis }));
      }
    } catch (err) {
      console.error('Failed to generate comparative analysis:', err);
    }
  };

  const renderByPromptView = () => {
    if (!results) return null;
    
    const promptVersions = [results.original_prompt, ...results.improved_prompts];
    const responsesByVersion: any = {};
    
    // Group responses by prompt version
    results.llm_responses?.forEach((response: any) => {
      const version = response.prompt_version;
      if (!responsesByVersion[version]) {
        responsesByVersion[version] = [];
      }
      responsesByVersion[version].push(response);
    });
    
    return (
      <PromptVersionGrid>
        {promptVersions.map((version: any) => {
          const responses = responsesByVersion[version.version_type] || [];
          const bestScore = Math.max(...(results.evaluations?.map((e: any) => e.overall_score) || [0]));
          const currentScore = results.evaluations?.find((e: any, i: number) => 
            promptVersions[i]?.version_type === version.version_type
          )?.overall_score || 0;
          
          return (
            <VersionCard key={version.version_type} $isWinner={currentScore === bestScore}>
              <CardTitle>
                {version.version_type === 'original' ? '📝 Original' :
                 version.version_type === 'promptasaurus' ? '✨ Promptasaurus' : '🦕 Fossil Fills'}
              </CardTitle>
              
              <SectionLabel>Prompt</SectionLabel>
              <PromptText>{version.prompt_text}</PromptText>
              
              {responses.map((response: any, idx: number) => (
                <div key={idx}>
                  <SectionLabel>
                    {response.model_used?.includes('openai') ? 'OpenAI Response' : 'Anthropic Response'}
                  </SectionLabel>
                  <ResponseText>{response.response_text}</ResponseText>
                </div>
              ))}
              
              <Score>
                <span>Score</span>
                <ScoreValue>{(currentScore * 100).toFixed(0)}%</ScoreValue>
              </Score>
            </VersionCard>
          );
        })}
      </PromptVersionGrid>
    );
  };

  const renderByLLMView = () => {
    if (!results || !canCompareLLMs) return renderByPromptView();
    
    const llmGroups: any = {
      openai: [],
      anthropic: []
    };
    
    // Group responses by LLM provider
    results.llm_responses?.forEach((response: any) => {
      if (response.model_used?.includes('openai')) {
        llmGroups.openai.push(response);
      } else if (response.model_used?.includes('anthropic')) {
        llmGroups.anthropic.push(response);
      }
    });
    
    return (
      <LLMComparisonGrid>
        {Object.entries(llmGroups).map(([provider, responses]: [string, any]) => (
          responses.length > 0 && (
            <LLMSection key={provider}>
              <LLMHeader>
                {provider === 'openai' ? '🤖 OpenAI GPT-4o-mini' : '🧠 Anthropic Claude 3.5 Sonnet'}
              </LLMHeader>
              <PromptVersionGrid>
                {(responses as any[]).map((response: any) => (
                  <VersionCard key={response.prompt_version}>
                    <CardTitle>
                      {response.prompt_version === 'original' ? '📝 Original' :
                       response.prompt_version === 'promptasaurus' ? '✨ Promptasaurus' : '🦕 Fossil Fills'}
                    </CardTitle>
                    <ResponseText>{response.response_text}</ResponseText>
                  </VersionCard>
                ))}
              </PromptVersionGrid>
            </LLMSection>
          )
        ))}
      </LLMComparisonGrid>
    );
  };

  return (
    <DemoContainer>
      <Title>🚀 Advanced Prompt Comparison</Title>
      
      {showUpgradePrompt && !canCompareLLMs && (
        <UpgradeBanner>
          <UpgradeTitle>🔒 Unlock Multi-LLM Comparison</UpgradeTitle>
          <UpgradeDescription>
            Compare responses from OpenAI and Anthropic side-by-side to see which AI performs better for your specific use case.
            This premium feature is available for Raptor and Rex tier users.
          </UpgradeDescription>
          <Button as={Link} to="/pricing" variant="secondary" size="large">
            View Pricing Plans
          </Button>
          <button 
            style={{ 
              marginLeft: theme.spacing.md, 
              background: 'transparent', 
              border: '1px solid white', 
              color: 'white', 
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              borderRadius: theme.radii.md,
              cursor: 'pointer'
            }}
            onClick={() => setShowUpgradePrompt(false)}
          >
            Continue with Single Provider
          </button>
        </UpgradeBanner>
      )}
      
      <ProviderSelector>
        <h3>Select AI Providers</h3>
        <ProviderGrid>
          <ProviderCard 
            $selected={selectedProviders.includes('openai')}
            onClick={() => toggleProvider('openai')}
          >
            <ProviderTitle>🤖 OpenAI</ProviderTitle>
            <ProviderDescription>GPT-4o-mini - Latest, fast and cheap</ProviderDescription>
          </ProviderCard>
          
          <ProviderCard 
            $selected={selectedProviders.includes('anthropic')}
            onClick={() => toggleProvider('anthropic')}
          >
            <ProviderTitle>🧠 Anthropic</ProviderTitle>
            <ProviderDescription>Claude 3 Haiku - Efficient and nuanced</ProviderDescription>
          </ProviderCard>
          
          <ProviderCard 
            $selected={selectedProviders.length === 2}
            onClick={() => toggleProvider('both')}
            style={{ opacity: canCompareLLMs ? 1 : 0.7 }}
          >
            <ProviderTitle>
              {canCompareLLMs ? '⚡' : '🔒'} Both Providers
            </ProviderTitle>
            <ProviderDescription>
              Compare side-by-side {canCompareLLMs ? '' : '(Raptor/Rex only)'}
            </ProviderDescription>
          </ProviderCard>
        </ProviderGrid>
        
        {!canCompareLLMs && (
          <div style={{ marginTop: theme.spacing.md, color: theme.colors.text.muted }}>
            🔒 Upgrade to Rex tier to compare multiple LLMs simultaneously
          </div>
        )}
        
        <label style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginTop: theme.spacing.lg }}>
          <input 
            type="checkbox" 
            checked={useOwnKeys}
            onChange={(e) => setUseOwnKeys(e.target.checked)}
          />
          Use my own API keys
        </label>
        
        {useOwnKeys && (
          <ApiKeyInputs>
            {selectedProviders.includes('openai') && (
              <ApiKeyField>
                <ApiKeyLabel>OpenAI API Key</ApiKeyLabel>
                <ApiKeyInput
                  type="password"
                  placeholder="sk-..."
                  value={openaiKey}
                  onChange={(e) => setOpenaiKey(e.target.value)}
                />
              </ApiKeyField>
            )}
            
            {selectedProviders.includes('anthropic') && (
              <ApiKeyField>
                <ApiKeyLabel>Anthropic API Key</ApiKeyLabel>
                <ApiKeyInput
                  type="password"
                  placeholder="sk-ant-..."
                  value={anthropicKey}
                  onChange={(e) => setAnthropicKey(e.target.value)}
                />
              </ApiKeyField>
            )}
          </ApiKeyInputs>
        )}
      </ProviderSelector>
      
      <PromptInput>
        <TextArea
          placeholder="Enter your prompt here..."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
      </PromptInput>
      
      <Button 
        onClick={runComparison}
        disabled={loading || !prompt.trim() || selectedProviders.length === 0}
        size="large"
      >
        {loading ? 'Comparing...' : 'Run Advanced Comparison'}
      </Button>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      {loading && <LoadingSpinner text="Generating advanced comparisons..." />}
      
      {results && (
        <>
          {canCompareLLMs && selectedProviders.length > 1 && (
            <ViewToggle>
              <ViewButton 
                $active={viewMode === 'by-prompt'}
                onClick={() => setViewMode('by-prompt')}
              >
                View by Prompt Version
              </ViewButton>
              <ViewButton 
                $active={viewMode === 'by-llm'}
                onClick={() => setViewMode('by-llm')}
              >
                View by LLM Provider
              </ViewButton>
            </ViewToggle>
          )}
          
          <ComparisonContainer>
            {viewMode === 'by-prompt' ? renderByPromptView() : renderByLLMView()}
          </ComparisonContainer>
          
          {results.comparative_analysis && (
            <ComparisonAnalysis>
              <AnalysisTitle>🔍 Comparative Analysis</AnalysisTitle>
              <AnalysisContent>{results.comparative_analysis}</AnalysisContent>
            </ComparisonAnalysis>
          )}
          
          {results.comparison_summary && (
            <ComparisonAnalysis>
              <AnalysisTitle>📊 Performance Summary</AnalysisTitle>
              <AnalysisContent>{results.comparison_summary}</AnalysisContent>
            </ComparisonAnalysis>
          )}
        </>
      )}
    </DemoContainer>
  );
};